import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ShowcaseLoginOutcome, ShowcasesApiService } from '@birco/rainmesh-api';
import { firstValueFrom } from 'rxjs';
import { RouteConstants } from '../../../../shared/constants/route-constants';
import { DemoLoginTokenService } from '../../../../core/services/demo-login-token.service';
import { ShowcaseSessionService } from '../../../../core/services/showcase-session.service';
import { CardComponent } from '../../../../shared/components/card/card.component';
import { TranslatePipe } from '@ngx-translate/core';

type PageState =
  | 'locationRequesting'
  | 'locationDenied'
  | 'loggingIn'
  | 'loginError'
  | 'tooFarAway'
  | 'groupExpired'
  | 'inUse'
  | 'locationError';

@Component({
  selector: 'app-page-demo-login',
  imports: [CardComponent, TranslatePipe],
  templateUrl: './page-demo-login.component.html',
  styleUrl: './page-demo-login.component.scss',
})
export class PageDemoLoginComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly apiService = inject(ShowcasesApiService);
  private readonly demoLoginTokenService = inject(DemoLoginTokenService);
  private readonly showcaseSessionService = inject(ShowcaseSessionService);

  state: PageState = 'locationRequesting';

  private location?: { lat: number; lon: number };

  ngOnInit(): void {
    this.requestLocation();
  }

  requestLocation(): void {
    if (!navigator.geolocation) {
      this.state = 'locationDenied';
      return;
    }

    this.state = 'locationRequesting';

    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.location = {
          lat: position.coords.latitude,
          lon: position.coords.longitude,
        };
        this.loginAndRedirect();
      },
      () => {
        this.state = 'locationDenied';
      },
    );
  }

  retryLogin(): void {
    // Re-request the location instead of reusing the previously captured one:
    // the client's position may have changed since the last attempt.
    this.requestLocation();
  }

  private async loginAndRedirect(): Promise<void> {
    this.state = 'loggingIn';

    const scenarioId = this.route.snapshot.paramMap.get('scenarioId');
    if (!scenarioId) {
      this.state = 'loginError';
      return;
    }

    if (!this.location) {
      this.state = 'locationError';
      return;
    }

    const groupId = this.route.snapshot.queryParamMap.get('groupId');

    this.demoLoginTokenService.clearAccessToken();

    try {
      const { outcome, accessToken } = await firstValueFrom(
        this.apiService.loginToShowcase(scenarioId, {
          latitude: this.location.lat,
          longitude: this.location.lon,
          groupId,
        }),
      );

      if (outcome === ShowcaseLoginOutcome.TOO_FAR_AWAY) {
        this.state = 'tooFarAway';
        return;
      }

      if (outcome === ShowcaseLoginOutcome.IN_USE) {
        this.state = 'inUse';
        return;
      }

      if (outcome === ShowcaseLoginOutcome.GROUP_EXPIRED) {
        this.state = 'groupExpired';
        return;
      }

      if (
        !accessToken ||
        !this.demoLoginTokenService.persistAccessToken(accessToken)
      ) {
        this.state = 'loginError';
        return;
      }

      this.showcaseSessionService.startSession(scenarioId);
      await this.router.navigate(['/', RouteConstants.routeDemo]);
    } catch (error) {
      console.error('Error logging in to showcase:', error);
      this.state = 'loginError';
    }
  }
}
